import type { RootState } from "store";
import { ActionTree } from "vuex";
import { DashboardService } from "../service";
import timesheetService from "@/services/timesheet.service";
import {
    DashboardState,
    FilterTimeSheet,
    SET_COUNT_NOTI_UNREAD,
    SET_COUNT_TIME_SHEET_STATUS,
    SET_LIST_TIME_SHEET, SET_LIST_TIME_SHEET_EVENT,
    timeSheetStatusName,
    SET_TOTAL_HOUR_TIME_SHEET,
    SET_LIST_TIME_SHEET_LINE
} from "./types";
import dayjs from "dayjs";
import { NotificationService } from "modules/notifications/service";
import { SET_USER } from "modules/auth/store/types";


const service = new DashboardService();
const notificationService = new NotificationService();
export const actions: ActionTree<DashboardState, RootState> = {
    // New action using the modern API endpoint
    async actLoadTimeSheetsWithApi({ commit }, params: any) {
        const filterTimeSheet: FilterTimeSheet = this.getters["dashboard/filterTimeSheet"];
        const lstTimeSheet = this.getters["dashboard/timeSheets"];
        
        // Prepare parameters for the API in the format expected by TimesheetExpandGetInput
        const apiParams: any = {
            _Skip: params.page ? (params.page - 1) * 20 : 0,
            _Top: 25,
            _OrderBy: "CreatedOn",
            _OrderType: "desc"
        };

        // Map filter parameters
        if (filterTimeSheet.timeSheetId) {
            apiParams.TimesheetHeaderId = filterTimeSheet.timeSheetId;
        }
        
        if (filterTimeSheet.fromDay && filterTimeSheet.toDay) {
            apiParams.FromDate = filterTimeSheet.fromDay;
            apiParams.ToDate = filterTimeSheet.toDay;
        }
        
        if (filterTimeSheet.timeSheetStatus !== undefined) {
            apiParams.Status = filterTimeSheet.timeSheetStatus;
        }
        
        // Call the new API endpoint
        const response = await service.fetchTimeSheetsWithParams(apiParams);
        
        if (!response || !response.value) {
            commit(SET_LIST_TIME_SHEET, [...lstTimeSheet]);
            return;
        }
        // Process the returned data
        const data = response.value.map((item: TimeSheetHeader) => {
            const timeSheet: TimeSheetHeader = {
                ...item
            };
            
            if (timeSheet.StartDate) {
                timeSheet.StartDate = dayjs(timeSheet.StartDate).format('MMM DD, YYYY');
            }
            
            if (timeSheet.TimesheetStatus !== undefined) {
                timeSheet.TimesheetStatusName = timeSheetStatusName[timeSheet.TimesheetStatus as keyof typeof timeSheetStatusName];
            }
            
            if (item.TimesheetLines && item.TimesheetLines.length > 0) {
                timeSheet.TimesheetLines = item.TimesheetLines.map((line: any) => {
                    const timesheetLine: TimeSheetLine = {
                        ...line
                    };
                    
                    timesheetLine.JobTitle = '---';
                    
                    if (line.TimesheetLineType === 0) {
                        timesheetLine.JobType = 'Rental';
                    } else if (line.TimesheetLineType === 1) {
                        timesheetLine.JobType = 'Unbill';
                    } else if (line.TimesheetLineType === 2) {
                        timesheetLine.JobType = 'Job cost';
                    }
                    
                    if (line.WorkOrderTaskId && line.WorkOrderTask) {
                        timesheetLine.JobTitle = `${line.WorkOrderTask.Name} ${line.WorkOrderTask.Description}`;
                    }
                    
                    if (line.JobCostCodeId && line.JobCostCode) {
                        timesheetLine.JobTitle = `${line.JobCostCode.Name} ${line.JobCostCode.Description}`;
                    }
                    
                    if (line.Glaccount && line.GlaccountId) {
                        timesheetLine.JobTitle = `${line.Glaccount.Description}`;
                    }
                    
                    if (line.TimesheetLineMinutes && line.TimesheetLineMinutes > 0) {
                        timesheetLine.TimesheetLineMinutesTitle = `${Math.floor(line.TimesheetLineMinutes / 60)}h ${line.TimesheetLineMinutes % 60}min`;
                    }
                    
                    return timesheetLine;
                });
            }
            
            if (timeSheet.TotalDuration && timeSheet.TotalDuration > 0) {
                timeSheet.TotalWorkingTitle = `${Math.floor(timeSheet.TotalDuration / 60)}h ${timeSheet.TotalDuration % 60}min`;
                timeSheet.TotalWorkingHours = timeSheet.TotalDuration / 60;
            } else {
                timeSheet.TotalWorkingTitle = '0h';
                timeSheet.TotalWorkingHours = 0;
            }
            
            return timeSheet;
        });
        
        commit(SET_LIST_TIME_SHEET, [...lstTimeSheet, ...data]);
    },

    async actCountTimeSheetStatus({ commit }) {
        const filterTimeSheet: FilterTimeSheet = this.getters["dashboard/filterTimeSheet"];

        const params: Record<string, string | number> = {};
        if (filterTimeSheet.fromDay && filterTimeSheet.toDay) {
            params.fromDate = filterTimeSheet.fromDay;
            params.toDate = filterTimeSheet.toDay;
        }
        if (filterTimeSheet.timeSheetId) {
            params.searchId = filterTimeSheet.timeSheetId;
        }

        const response = await timesheetService.getStatusCounts(params);
        const data = {
            total: response.all ?? 0,
            totalDraft: response.draft ?? 0,
            totalPendingApproved: response.pending ?? 0,
            totalApproved: response.approved ?? 0,
            totalDeclined: response.declined ?? 0,
            totalCanceled: response.cancelled ?? 0,
        };
        commit(SET_COUNT_TIME_SHEET_STATUS, data);
        return data;
    },

    async actGetTimeSheet({ commit }, params: any) {
        const expand = `TimesheetLines($expand=TimesheetAdditionals($select=TimesheetAdditionalId,Manufacturer,Model,Smureading,SerialNumber,CustomerName,CustomerEmail,Locations,TravelKms,
                Rego,WorkerSignature,CustomerSignature,AdditionalInfo,CustomerAcceptanceName))
        ,Approver,User`
        const select = `EndTime,Shift,StartDate,StartTime,SubmittedCount,TimesheetHeaderId,TimesheetStatus,UserId`
        const result = await service.fetchTimeSheetById(params.id, expand, select)
        return result
    },

    async actGetTimeSheetEvent({ commit }, params: any) {
        const countNotiUnRead = await notificationService.fetchCountNotiUnRead()
        const result = await notificationService.fetchNotification(1, 1000000)
        commit(SET_LIST_TIME_SHEET_EVENT, result)
        commit(SET_COUNT_NOTI_UNREAD, countNotiUnRead)
        return result
    },

    async actFetchMessage({ commit }, params: any) {
        const filters = `TimesheetHeaderId eq ${params.TimesheetHeaderId}`
        const expand = `TimesheetEventNotifications(expand=User(select=FirstName,LastName,Email,AvatarUrl))`
        const result = await service.fetchTimesheetEvent(expand, filters)
        const messageModel: MessageEventModel[] = []
        if (result.value) {
            result.value.forEach((timesheetEvent: TimesheetEvent) => {
                if (timesheetEvent.TimesheetEventNotifications) {
                    timesheetEvent.TimesheetEventNotifications.forEach((timesheetEventNotification) => {
                        messageModel.push({
                            TimesheetEventDateTime: timesheetEventNotification.TimesheetEventNotificationDateTime,
                            TimesheetEventId: timesheetEvent.TimesheetEventId,
                            TimesheetEventType: timesheetEvent.TimesheetEventType,
                            User: timesheetEventNotification.User,
                            Message: timesheetEventNotification.Message,
                            TimesheetEventNotificationId: timesheetEventNotification.TimesheetEventNotificationId
                        })
                    })
                }
            });
        }
        return messageModel
    },

};
